import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useHistory, useLocation } from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../../services/use-auth";
import Input from "../../../components/Form/Input";
import usePageMeta from "../../../services/page-meta";

function Group({ id, title, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <fieldset id={id} className="form-group js-form-group" style={{ border: '1px solid #e0e0e0', borderRadius: 6, marginBottom: 16 }}>
            <legend className="form-label" style={{ fontWeight: 700, padding: '0 8px 16px' }}>
                {title}
            </legend>
            <div style={{ display: open ? 'block' : 'none', padding: '8px 12px 12px' }}>{children}</div>
        </fieldset>
    );
}

const schema = yup.object({
    firstName: yup.string().trim().required("Вкажіть ім’я"),
    lastName: yup.string().trim().required("Вкажіть прізвище"),
    middleName: yup.string().trim().nullable(),
});

function resolveFrom(state) {
    const from = state?.from || "/";
    return typeof from === "string" ? from : from?.pathname || "/";
}

export default function ProfileCompletePage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.profileComplete.title', 'Complete profile'),
        description: t('meta.profileComplete.description', t('app.description')),
        keywords: t('meta.profileComplete.keywords', t('app.keywords')),
    });

    const auth = useAuth();
    const user = auth?.user || null;
    const history = useHistory();
    const location = useLocation();

    const defaultValues = useMemo(() => ({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        middleName: user?.middleName || "",
    }), [user?.firstName, user?.lastName, user?.middleName]);

    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues,
    });

    const alreadyComplete = !!(user && user.firstName && user.lastName);
    useEffect(() => {
        if (alreadyComplete) {
            history.replace(resolveFrom(location.state));
        }
    }, [alreadyComplete, history, location.state]);

    const [error, setError] = useState("");

    const onSubmit = async (values) => {
        setError("");
        try {
            const res = await fetch("/api/v1/profile/complete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    middleName: values.middleName?.trim() || '',
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || "Не вдалося зберегти профіль");

            if (auth?.setUser) {
                auth.setUser({
                    ...(user || {}),
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    middleName: values.middleName?.trim() || "",
                });
            }

            history.replace(resolveFrom(location.state));
        } catch (e) {
            setError(e.message || "Сталася помилка");
        }
    };

    const firstName = watch("firstName");
    const lastName = watch("lastName");
    const middleName = watch("middleName");

    return (
        <section className="section flex-center">
            <div className="authentication-form registration-form" style={{ width: "100%", maxWidth: 640 }}>
                <h1>Завершення профілю</h1>
                <p>Будь ласка, заповніть ім’я, прізвище та, за бажанням, по батькові.</p>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Group id="grp-fio" title="ПІБ" defaultOpen>
                        <div className="form-group block-static-height js-form-group">
                            <Input
                                watch={() => ({ firstName, lastName, middleName })}
                                type="text"
                                name="firstName"
                                id="firstName"
                                register={register}
                                inputClassName="form-input"
                                labelClassName="form-label"
                                label="Ім’я"
                                autoComplete="off"
                            />
                            {errors?.firstName && <span className="error_text">{errors.firstName.message}</span>}
                        </div>

                        <div className="form-group block-static-height js-form-group">
                            <Input
                                watch={() => ({ firstName, lastName, middleName })}
                                type="text"
                                name="lastName"
                                id="lastName"
                                register={register}
                                inputClassName="form-input"
                                labelClassName="form-label"
                                label="Прізвище"
                                autoComplete="off"
                            />
                            {errors?.lastName && <span className="error_text">{errors.lastName.message}</span>}
                        </div>

                        <div className="form-group block-static-height js-form-group">
                            <Input
                                watch={() => ({ firstName, lastName, middleName })}
                                type="text"
                                name="middleName"
                                id="middleName"
                                register={register}
                                inputClassName="form-input"
                                labelClassName="form-label"
                                label="По батькові (необов’язково)"
                                autoComplete="off"
                            />
                        </div>
                    </Group>

                    {error && <div className="error_text" style={{ marginBottom: 12 }}>{error}</div>}

                    <div className="submit-block">
                        <button type="submit" className="btn" disabled={isSubmitting}>
                            {isSubmitting ? "Збереження…" : "Зберегти"}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
